import { useState, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Plus, Search, Users, HardHat, UsersRound, Archive, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import API, { apiError } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { ErpHeading, ErpStatus, ErpPagination, useErpList } from '../../components/erp/ErpShared';
import { PersonForm, TeamForm } from '../../components/erp/PeopleForms';
import '../../components/erp/assets.css';

const STATUS_CLS = { activ: 'text-emerald-400 bg-emerald-500/10', inactiv: 'text-zinc-400 bg-zinc-500/10', concediu: 'text-amber-400 bg-amber-500/10' };
const fmt = s => s ? new Date(s).toLocaleDateString('ro-RO') : '—';

function ResourceList({ group, canWrite, renderRow, openForm, refreshKey }) {
  const [q, setQ] = useState('');
  const query = useMemo(() => `${q ? `&q=${encodeURIComponent(q)}` : ''}&_r=${refreshKey}`, [q, refreshKey]);
  const list = useErpList(`/erp/people/${group}`, query);
  const archive = async (rid) => { const reason = window.prompt('Motivul arhivării?'); if (!reason) return;
    try { await API.delete(`/erp/people/${group}/${rid}`, { data: { reason } }); toast.success('Arhivat'); list.reload(); } catch (e) { toast.error(apiError(e)); } };
  return <>
    <div className="flex flex-wrap gap-3 items-center mb-4">
      <div className="relative flex-1 min-w-[200px]"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Caută…" className="pl-9" data-testid={`search-${group}`} /></div>
      {canWrite && <Button onClick={() => openForm({})} data-testid={`add-${group}`}><Plus className="w-4 h-4 mr-2" />Adaugă</Button>}
    </div>
    <ErpStatus list={list} />
    {!list.loading && !list.error && <div className="space-y-2" data-testid={`list-${group}`}>{list.items.map(it => <div key={it.id} className="erp-record !border rounded-xl px-4" style={{ borderColor: 'hsl(var(--border))' }} data-testid={`row-${it.id}`}>
      <div className="flex-1 min-w-0">{renderRow(it)}</div>
      {canWrite && <div className="flex gap-1 shrink-0"><Button size="icon" variant="ghost" onClick={() => openForm(it)} data-testid={`edit-${it.id}`}><Pencil className="w-4 h-4" /></Button>
        <Button size="icon" variant="ghost" onClick={() => archive(it.id)}><Archive className="w-4 h-4 text-rose-400" /></Button></div>}
    </div>)}</div>}
    {!list.loading && !list.error && list.items.length > 0 && <ErpPagination list={list} />}
  </>;
}

export default function PeoplePage() {
  const { permissions } = useOutletContext();
  const canWrite = permissions.includes('people.write');
  const [empForm, setEmpForm] = useState(null);
  const [subForm, setSubForm] = useState(null);
  const [teamForm, setTeamForm] = useState(null);
  const [v, setV] = useState({ employees: 0, subcontractors: 0, teams: 0 });
  const bump = g => setV(s => ({ ...s, [g]: s[g] + 1 }));

  return <>
    <ErpHeading title="Echipe & Subcontractanți" />
    <Tabs defaultValue="employees">
      <TabsList><TabsTrigger value="employees" data-testid="ptab-employees"><Users className="w-4 h-4 mr-2" />Angajați</TabsTrigger>
        <TabsTrigger value="subcontractors" data-testid="ptab-subcontractors"><HardHat className="w-4 h-4 mr-2" />Subcontractanți</TabsTrigger>
        <TabsTrigger value="teams" data-testid="ptab-teams"><UsersRound className="w-4 h-4 mr-2" />Echipe mixte</TabsTrigger></TabsList>

      <TabsContent value="employees" className="pt-5">
        <ResourceList group="employees" canWrite={canWrite} openForm={setEmpForm} refreshKey={v.employees} renderRow={it => <>
          <div className="flex items-center gap-2 flex-wrap"><span className={`dot ${it.doc_status?.state || 'none'}`} /><p className="font-medium truncate">{it.name}</p>
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_CLS[it.status] || ''}`}>{it.status}</span></div>
          <p className="text-xs text-muted-foreground mt-1">{it.position || '—'}{it.skills?.length ? ` · ${it.skills.join(', ')}` : ''}{it.doc_status?.soonest ? ` · doc. exp. ${fmt(it.doc_status.soonest)}` : ''}</p></>} />
      </TabsContent>
      <TabsContent value="subcontractors" className="pt-5">
        <ResourceList group="subcontractors" canWrite={canWrite} openForm={setSubForm} refreshKey={v.subcontractors} renderRow={it => <>
          <div className="flex items-center gap-2 flex-wrap"><span className={`dot ${it.doc_status?.state || 'none'}`} /><p className="font-medium truncate">{it.name}</p>
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_CLS[it.status] || ''}`}>{it.status}</span></div>
          <p className="text-xs text-muted-foreground mt-1">{it.registration_number || '—'}{it.contact_name ? ` · ${it.contact_name}` : ''}{it.qualifications?.length ? ` · ${it.qualifications.join(', ')}` : ''}</p></>} />
      </TabsContent>
      <TabsContent value="teams" className="pt-5">
        <ResourceList group="teams" canWrite={canWrite} openForm={setTeamForm} refreshKey={v.teams} renderRow={it => <>
          <p className="font-medium truncate">{it.name}{it.team_type ? <span className="text-xs text-muted-foreground ml-2">· {it.team_type}</span> : null}</p>
          <p className="text-xs text-muted-foreground mt-1">{(it.members?.length || 0)} membri{it.members?.some(m => m.origin === 'extern') ? ' (mixt)' : ''}{it.valid_until ? ` · până la ${fmt(it.valid_until)}` : ''}</p></>} />
      </TabsContent>
    </Tabs>

    {empForm && <PersonForm variant="employee" record={empForm.id ? empForm : null} onClose={() => setEmpForm(null)} onSaved={() => bump('employees')} />}
    {subForm && <PersonForm variant="subcontractor" record={subForm.id ? subForm : null} onClose={() => setSubForm(null)} onSaved={() => bump('subcontractors')} />}
    {teamForm && <TeamForm record={teamForm.id ? teamForm : null} onClose={() => setTeamForm(null)} onSaved={() => bump('teams')} />}
  </>;
}
